import type { ReactNode } from 'react';
import { AppBar, Box, Container, Link, Stack, Toolbar, Typography } from '@mui/material';
import { NavLink } from 'react-router-dom';

interface Props { children: ReactNode }

const NAV = [
  { to: '/leaderboard',    label: 'Leaderboard' },
  { to: '/dilemmas',       label: 'Dilemmas' },
  { to: '/visualizations', label: 'Visualizations' },
  { to: '/heatmap',        label: 'Heatmap' },
  { to: '/whitepaper',     label: 'Whitepaper' },
  { to: '/faq',            label: 'FAQ' },
  { to: '/sponsors',       label: 'Sponsors' },
] as const;

export function Layout({ children }: Props) {
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <AppBar position="sticky" color="default" elevation={0} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
        <Toolbar sx={{ gap: { xs: 1.5, md: 3 }, minHeight: { xs: 56, md: 64 }, px: { xs: 1.5, md: 3 } }}>
          <Link
            component={NavLink}
            to="/"
            underline="none"
            sx={{
              color: 'text.primary', fontWeight: 700, letterSpacing: '-0.01em',
              fontSize: { xs: 14, md: 16 },
              whiteSpace: 'nowrap', flexShrink: 0,
            }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Gödel&apos;s Therapy Room</Box>
            <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>GTR</Box>
          </Link>
          <Stack direction="row" spacing={{ xs: 1.25, md: 2 }} sx={{
            ml: { xs: 0.5, md: 2 },
            overflowX: 'auto',
            // Hide the scrollbar on the nav strip on small screens — the
            // horizontal-scroll affordance is via touch flick.
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
          }}>
            {NAV.map((item) => (
              <Link
                key={item.to}
                component={NavLink}
                to={item.to}
                underline="none"
                sx={{
                  color: 'text.secondary',
                  fontSize: { xs: 13, md: 14 },
                  whiteSpace: 'nowrap',
                  '&.active': { color: 'primary.main' },
                  '&:hover': { color: 'text.primary' },
                }}
              >
                {item.label}
              </Link>
            ))}
          </Stack>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ flex: 1, py: { xs: 2.5, md: 4 }, px: { xs: 2, md: 3 } }}>
        {children}
      </Container>

      <Box
        component="footer"
        sx={{
          borderTop: '1px solid', borderColor: 'divider', py: 3, px: 4,
          display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2,
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          gtr.dev — an interrogation chamber for cognition.
        </Typography>
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
          <Link
            component={NavLink}
            to="/privacy"
            underline="hover"
            variant="caption"
            sx={{ color: 'text.disabled' }}
          >
            Privacy
          </Link>
          <Link
            href="https://deepforkcyber.com"
            target="_blank"
            rel="noreferrer"
            underline="hover"
            variant="caption"
            sx={{ color: 'text.disabled' }}
          >
            Deep Fork Cyber
          </Link>
        </Stack>
      </Box>
    </Box>
  );
}
